import React, { useState, useEffect, useRef } from 'react';
import StoryReels from './StoryReels';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

const ReelsCarousel = ({ stories }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [userHasEnabledSound, setUserHasEnabledSound] = useState(false);
  const containerRef = useRef(null);

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;

  // Handle sound preference from StoryReels
  const handleSoundEnabled = () => {
    setUserHasEnabledSound(true);
  };

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && currentIndex < stories.length - 1) {
      // Swipe left - go to next video
      setCurrentIndex(prev => prev + 1);
      setIsPlaying(true);
    } else if (isRightSwipe && currentIndex > 0) {
      // Swipe right - go to previous video
      setCurrentIndex(prev => prev - 1);
      setIsPlaying(true);
    }
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'ArrowRight' && currentIndex < stories.length - 1) {
        setCurrentIndex(prev => prev + 1);
        setIsPlaying(true);
      } else if (e.key === 'ArrowLeft' && currentIndex > 0) {
        setCurrentIndex(prev => prev - 1);
        setIsPlaying(true);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentIndex, stories.length]);

  // Auto-play current video when index changes
  useEffect(() => {
    if (isPlaying) {
      // Small delay to ensure the video element is ready
      const timer = setTimeout(() => {
        const videoElement = containerRef.current?.querySelector('video');
        if (videoElement) {
          videoElement.play().catch(error => {
            console.log('Auto-play prevented:', error);
          });
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [currentIndex, isPlaying]);

  const goToNext = () => {
    if (currentIndex < stories.length - 1) {
      setCurrentIndex(prev => prev + 1);
      setIsPlaying(true);
    }
  };

  const goToPrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
      setIsPlaying(true);
    }
  };

  if (!stories || stories.length === 0) {
    return (
      <div className="flex items-center justify-center h-96 bg-gray-100 rounded-lg">
        <p className="text-gray-500">No video stories available</p>
      </div>
    );
  }

  const currentStory = stories[currentIndex];

  return (
    <div className="relative w-full max-w-sm mx-auto">
      {/* Main Video Container */}
      <div 
        ref={containerRef}
        className="relative bg-black rounded-lg overflow-hidden"
        style={{ aspectRatio: '9/16' }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* Current Video */}
        <StoryReels 
          story={currentStory} 
          key={currentStory.id}
          autoPlay={isPlaying}
          onSoundEnabled={handleSoundEnabled}
          userHasEnabledSound={userHasEnabledSound}
        />

        {/* Left Arrow */}
        {currentIndex > 0 && (
          <button
            onClick={goToPrevious}
            className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-all duration-200 z-10"
          >
            <ChevronLeftIcon className="h-6 w-6" />
          </button>
        )}

        {/* Right Arrow */}
        {currentIndex < stories.length - 1 && (
          <button
            onClick={goToNext}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-all duration-200 z-10"
          >
            <ChevronRightIcon className="h-6 w-6" />
          </button>
        )}

        {/* Navigation Indicators */}
        <div className="absolute top-4 right-4 flex space-x-1">
          {stories.map((_, index) => (
            <div
              key={index}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                index === currentIndex 
                  ? 'bg-white' 
                  : 'bg-white/30'
              }`}
            />
          ))}
        </div>

        {/* Video Counter */}
        <div className="absolute top-4 left-4 bg-black/50 text-white px-2 py-1 rounded text-sm">
          {currentIndex + 1} / {stories.length}
        </div>

        {/* Navigation Instructions */}
        <div className="absolute bottom-20 left-4 text-white/70 text-xs">
          <p>Swipe left for next</p>
          <p>Swipe right for previous</p>
        </div>
      </div>
    </div>
  );
};

export default ReelsCarousel; 